/**
 * Data hook for assigning a distributor (supplier entity) to a chat (#123).
 *
 * Legacy admin only lets the distributor be assigned while the chat is
 * `requested` (see requestedChatLifecycle.canAssignDistributor) — the caller
 * is responsible for that gating; this hook performs the assignment.
 *
 * chat-service's legacy `chatUpdate` expects `supplierId` to be the supplier's
 * bot USER id (it resolves the entity from that user internally), exactly like
 * `chatCreate`. The distributor picker yields the ENTITY id, so we resolve the
 * bot user id first (reusing ChatsPageGetSupplierBotUser) before mutating.
 *
 * After assignment the chat is refetched (GetChatData) so the UI reconciles.
 */
import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useChatUpdateDataMutationMutation } from '../../../generated';
import { ChatsPageGetSupplierBotUserQuery } from '../data/queries/getSupplierBotUser.gql';

/** Shape of the ChatsPageGetSupplierBotUser query response. */
interface ChatsPageGetSupplierBotUserData {
  supplier?: {
    id: string;
    botUser?: {
      id: string;
    } | null;
  } | null;
}

interface ChatsPageGetSupplierBotUserVariables {
  id: string;
}

export interface UseAssignChatSupplierConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseAssignChatSupplierResponse {
  assignSupplier: (variables: {
    chatId: string;
    supplierEntityId: string;
  }) => Promise<void>;
  isAssigning: boolean;
  error: Error | null;
}

export const useAssignChatSupplier = ({
  onSuccess,
  onError,
}: UseAssignChatSupplierConfig = {}): UseAssignChatSupplierResponse => {
  const { addNotification } = useNotification();
  const apolloClient = useApolloClient();
  const [isResolving, setIsResolving] = useState(false);

  const [assignSupplierMutation, { loading: isMutating, error }] =
    useChatUpdateDataMutationMutation({
      errorPolicy: 'all',
      refetchQueries: ['GetChatData'],
      awaitRefetchQueries: true,
    });

  const assignSupplier = useCallback(
    async ({
      chatId,
      supplierEntityId,
    }: {
      chatId: string;
      supplierEntityId: string;
    }) => {
      try {
        setIsResolving(true);

        const { data: botUserData } = await apolloClient.query<
          ChatsPageGetSupplierBotUserData,
          ChatsPageGetSupplierBotUserVariables
        >({
          query: ChatsPageGetSupplierBotUserQuery,
          variables: { id: supplierEntityId },
          fetchPolicy: 'network-only',
        });

        const supplierBotUserId = botUserData?.supplier?.botUser?.id ?? null;
        if (!supplierBotUserId) {
          throw new Error(
            'This distributor has no bot user — assignment unavailable'
          );
        }

        const { data } = await assignSupplierMutation({
          variables: { id: chatId, chat: { supplierId: supplierBotUserId } },
        });

        if (!data?.chatUpdate) {
          throw new Error('Distributor assignment returned no chat data');
        }

        addNotification({
          title: 'Distributor Assigned',
          message: 'The distributor has been assigned to this chat.',
          type: 'success',
        });

        onSuccess?.();
      } catch (err) {
        const assignError =
          err instanceof Error
            ? err
            : new Error('Failed to assign distributor');

        addNotification({
          title: 'Assignment Failed',
          message: assignError.message,
          type: 'error',
        });

        onError?.(assignError);
        throw assignError;
      } finally {
        setIsResolving(false);
      }
    },
    [apolloClient, assignSupplierMutation, addNotification, onSuccess, onError]
  );

  return {
    assignSupplier,
    isAssigning: isResolving || isMutating,
    error: error ? new Error(error.message) : null,
  };
};
